import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { capitalize, uuid } from 'src/utilities/text';
import 'src/library/elements/d-loading-image';
import { registerLocalPlaceholder } from 'src/library/elements/pending-uploads';
import { AlertDialog } from 'src/library/components/alert-dialog';
import type { AlertDialogResult } from 'src/library/components/alert-dialog-result';
import { assetAvailability } from 'src/services/asset-availability';
import { removeScreenMedia } from 'src/store/signage';
import { getStore } from 'src/store';
import { getOrganizationId } from 'src/store/selectors/organization/getOrganizationId';
import type { LibraryItem } from 'src/library/screen/library-item';
import { resolveAssetUrl } from './resolve-asset-url';
import { getAssetUsage, invalidateAssetUsage } from './asset-usage';
import { slideDeleteButtonStyle } from 'src/content/screens/slide-delete-button-style';
import 'src/library/elements/d-label';
import { classMap } from 'lit/directives/class-map.js';
import { t } from 'src/i18n';

// Backend (PreviewSignageProvider.SIGNAGE_MEDIA_TYPES) aksepterer disse video-
// typene. Speiles eksplisitt i `accept` og i `extensionForMime` så filer som
// klienten godtar uten warning er filer backend faktisk vil prosessere.
const ACCEPT_MIME = [
  'image/*',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-matroska',
  'video/x-msvideo',
  'video/x-ms-wmv',
  'video/3gpp',
  'video/x-m4v',
].join(',');

// Matcher backend `PreviewSignageProvider.getExtensionForMime`. Default-fallback
// kan gi mismatch (f.eks. .quicktime → backend lagrer som .mov → 404), så vi
// holder den eksplisitte listen ved like.
function extensionForMime(mimeType: string): string {
  switch (mimeType.toLowerCase()) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/gif':
      return 'gif';
    case 'image/webp':
      return 'webp';
    case 'image/svg+xml':
      return 'svg';
    case 'video/mp4':
      return 'mp4';
    case 'video/webm':
      return 'webm';
    case 'video/quicktime':
      return 'mov';
    case 'video/x-matroska':
      return 'mkv';
    case 'video/x-msvideo':
      return 'avi';
    case 'video/x-ms-wmv':
      return 'wmv';
    case 'video/3gpp':
      return '3gp';
    case 'video/x-m4v':
      return 'm4v';
    default:
      return mimeType.split('/').pop() || 'bin';
  }
}

const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

/**
 */
@customElement('d-media-gallery')
export class DMediaGallery extends LitElement {
  static readonly styles = [
    slideDeleteButtonStyle,
    css`
      :host {
        display: flex;
        flex-direction: column;
        max-width: 456px;
        max-height: 380px;
        border-radius: 24px;
        background: white;
        filter: drop-shadow(0 2px 10px hsla(1, 0%, 0%, 0.3));
      }
      .header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        padding: 12px 16px 6px 16px;
        font-size: 17px;
      }
      .content {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        padding: 0 16px 16px;
        overflow-y: auto;
        cursor: default;
      }
      .media {
        position: relative;
        width: 100px;
        height: 100px;
        box-sizing: border-box;
        overflow: hidden;
        border-radius: 8px;
        cursor: pointer;
      }
      .upload {
        display: flex;
        justify-content: center;
        align-items: flex-end;
        border: 2px dashed var(--borderColorDark);
        background: url(/images/upload-gray.svg) 50% 0px no-repeat;
        background-size: 60px auto;
        padding: 12px;
        color: black;
        text-align: center;
        font-size: 13px;
        opacity: 0.5;
      }
      @media (hover: hover) {
        .upload:hover {
          border-style: solid;
          opacity: 0.8;
        }
      }
      .media d-loading-image {
        width: 100%;
        height: 100%;
      }
      .media d-loading-image::part(img) {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
      /* Alle tiles viser et kvadratisk, sentrert utsnitt (cover). Thumbnailen
         genereres nå aspekt-korrekt (sync-standard-library.sh -resize 256 0 /
         backend), så cover beskjærer til kvadrat uten å klemme bildet. Se
         #13085. */
      .media img.thumbnail,
      .media.video video.thumbnail {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
      }
      .media.video .thumbnail-placeholder {
        width: 100%;
        height: 100%;
        background: hsl(197, 35%, 22%);
      }
      /* Video-spesifikt: play-trekant overlay + hover-preview */
      .media.video::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 0;
        height: 0;
        border-left: 18px solid white;
        border-top: 12px solid transparent;
        border-bottom: 12px solid transparent;
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.6));
        pointer-events: none;
        transition: opacity 0.15s;
      }
      .media.video .hover-preview {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        opacity: 0;
        transition: opacity 0.15s;
        pointer-events: none;
      }
      @media (hover: hover) {
        .media.video:hover .hover-preview {
          opacity: 1;
        }
        .media.video:hover::after {
          opacity: 0;
        }
      }
    `,
  ];

  /**
   * Library-objekter for media-galleriet. `signageMediaType` brukes til å skille
   * video fra bilde (overlay-ikon, hover-preview). `assets[0]` er asset-pathen,
   * `thumbnail` er backend-generert data-URL (gjelder også for video).
   */
  @property({ type: Array })
  library: LibraryItem[] = [];

  @property({ type: String })
  assetBase = '';

  @query('#upload-input')
  uploadInput!: HTMLInputElement;

  /**
   * Henter orgId fra Redux store i stedet for å plumbe en prop gjennom
   * d-custom-slide → d-slide-media → her. Samme mønster som
   * `to-attachment-items.ts` for vedleggs-API.
   */
  private get organizationId(): number {
    return getOrganizationId(getStore().getState()) ?? 0;
  }

  /**
   * Tracker hvilke video-paths som er bekreftet i library, så tiles kan
   * bytte fra placeholder til ekte video-frame når backend-konvertering er
   * ferdig — uten at hver tile spammer 404 på asset-URL-en. Re-subscribes
   * når library-prop-en endres.
   */
  @state() private readyVideoPaths = new Set<string>();
  /**
   * Asset-paths som er referert fra slides på minst én skjerm. Brukes til
   * å skjule slett-knappen på items som er i bruk. Beregnes lazy ved
   * mount og når `organizationId` endres; refresh-cycle 30 s.
   */
  @state() private inUseAssets: Set<string> = new Set();
  @state() private isDeleting = false;
  private availabilityUnsubs: (() => void)[] = [];

  connectedCallback(): void {
    super.connectedCallback();
    this.refreshAvailabilitySubscriptions();
    void this.refreshUsage();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.clearAvailabilitySubscriptions();
  }

  protected updated(changedProps: Map<string, unknown>): void {
    if (changedProps.has('library')) {
      this.refreshAvailabilitySubscriptions();
    }
  }

  private async refreshUsage(): Promise<void> {
    const orgId = this.organizationId;
    if (!orgId) return;
    const paths = await getAssetUsage(orgId);
    if (!this.isConnected || this.organizationId !== orgId) return;
    this.inUseAssets = paths;
  }

  private clearAvailabilitySubscriptions(): void {
    this.availabilityUnsubs.forEach((u) => u());
    this.availabilityUnsubs = [];
  }

  private refreshAvailabilitySubscriptions(): void {
    this.clearAvailabilitySubscriptions();
    const next = new Set<string>();
    for (const item of this.library) {
      if (item.signageMediaType !== 'video') continue;
      const path = item.assets?.[0];
      if (!path) continue;
      const unsub = assetAvailability.subscribe(path, (ready) => {
        if (ready && !this.readyVideoPaths.has(path)) {
          this.readyVideoPaths = new Set([...this.readyVideoPaths, path]);
        }
      });
      this.availabilityUnsubs.push(unsub);
      if (assetAvailability.isReady(path)) next.add(path);
    }
    if (next.size !== this.readyVideoPaths.size) {
      this.readyVideoPaths = next;
    }
  }

  onUpload() {
    this.uploadInput.click();
  }

  private async onFileChange(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      await AlertDialog.open({
        title: t('screens.mediaGallery.fileTooLargeTitle'),
        content: t('screens.mediaGallery.fileTooLargeContent', {
          maxMb: Math.round(MAX_UPLOAD_BYTES / 1024 / 1024),
          fileMb: Math.round(file.size / 1024 / 1024),
        }),
      });
      return;
    }

    const isVideo = file.type.startsWith('video/');
    const mimeType = file.type || 'application/octet-stream';
    const inputExtension = extensionForMime(mimeType);
    const baseId = uuid();
    const name = `${baseId}.${inputExtension}`;
    // Backend (PreviewSignageProvider, slug-based asset spec v1.3) lagrer:
    //   - bilder via fast-path: `org/{slug}.{ext}` (samme ekstensjon som upload)
    //   - video etter konvertering: `org/{slug}.mp4` ALLTID (uansett input-format)
    // Slug = sanitizeSlug(name) som strip-er ekstensjonen, så for vår uuid
    // blir slug = baseId. Bruker vi feil ekstensjon i predicted URL-en (f.eks.
    // .mov for en mov-upload) får d-loading-video 404 i evighet — backend har
    // konvertert til mp4. Derfor: bytt til mp4 her for video.
    const playerExtension = isVideo ? 'mp4' : inputExtension;
    const mediaPath = `org/${baseId}.${playerExtension}`;
    const mediaType: 'image' | 'video' = isVideo ? 'video' : 'image';

    // Fila lastes opp direkte til S3 via presignert URL (#13237) — vi sender
    // selve File-objektet videre, ikke en base64 dataUrl.
    const dispatchUpload = () => {
      this.dispatchEvent(
        new CustomEvent<{ file: File; name: string; path: string; mediaType: 'image' | 'video'; mimeType: string }>(
          'upload-media',
          {
            bubbles: true,
            composed: true,
            detail: { file, name, path: mediaPath, mediaType, mimeType },
          },
        ),
      );
      this.select(mediaPath);
    };

    if (isVideo) {
      // Ingen placeholder: dataUrl er for stor å vise i editor (kan være hundrevis
      // av MB), og frame-ekstraksjon er upålitelig (Safari-quirk gir sort canvas).
      // d-loading-video viser en innebygget "lastes opp …"-overlay mens backend
      // konverterer.
      dispatchUpload();
      return;
    }

    // For bilde: les dataUrl-en kun som lokal placeholder — vi viser den i slide-en
    // til server-bildet er klart. Selve opplastingen bruker File-objektet.
    const reader = new FileReader();
    reader.onload = (e2) => {
      const dataUrl = (e2.target as FileReader).result as string;
      registerLocalPlaceholder(resolveAssetUrl(mediaPath, this.assetBase), dataUrl);
      dispatchUpload();
    };
    reader.readAsDataURL(file);
  }

  select(m: string) {
    this.dispatchEvent(
      new CustomEvent<{ src: string }>('select-media', {
        bubbles: true,
        composed: true,
        detail: { src: m },
      }),
    );
  }

  private startHoverPreview(e: MouseEvent) {
    const video = (e.currentTarget as HTMLElement).querySelector<HTMLVideoElement>('video.hover-preview');
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {
        /* autoplay-policy kan blokkere — preview er nice-to-have */
      });
    }
  }

  private stopHoverPreview(e: MouseEvent) {
    const video = (e.currentTarget as HTMLElement).querySelector<HTMLVideoElement>('video.hover-preview');
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  private canDelete(item: LibraryItem, path: string): boolean {
    // Bare user-uploaded items kan slettes (Trinnvis-stock har standardItem=true).
    // Skjul også for items som er i bruk på minst én skjerm — usage-sjekken
    // dekker hele organisasjonen, ikke bare gjeldende skjerm.
    if (item.standardItem) return false;
    if (this.isDeleting) return false;
    if (this.inUseAssets.has(path)) return false;
    if (item.id.startsWith('pending-')) return false;
    return true;
  }

  private async onDelete(e: MouseEvent, item: LibraryItem, path: string) {
    e.stopPropagation();
    const noun =
      item.signageMediaType === 'video'
        ? t('screens.mediaGallery.nounVideo')
        : item.signageMediaType === 'image'
          ? t('screens.mediaGallery.nounImage')
          : t('screens.mediaGallery.nounFile');
    if (!this.canDelete(item, path)) {
      await AlertDialog.open({
        title: t('screens.mediaGallery.cannotDeleteTitle'),
        content: capitalize(t('screens.mediaGallery.inUseCannotDelete', { noun })),
        noOption: true,
      });
    } else {
      if (this.isDeleting || !this.organizationId) return;
      // Brukeren har lastet opp filen som "{uuid}.{ext}" — filnavnet er ikke
      // gjenkjennelig. Refer til mediatypen i stedet ("denne videoen" /
      // "dette bildet" / generisk "denne filen").
      const result: AlertDialogResult = await AlertDialog.open({
        title: t('screens.mediaGallery.confirmDeleteTitle', { noun }),
        content: t('screens.mediaGallery.deletePermanent'),
      });
      if (result.action !== 'ok') return;
      this.isDeleting = true;
      try {
        await removeScreenMedia({ organizationId: this.organizationId, screenId: 0, id: item.id });
        invalidateAssetUsage(this.organizationId);
        // Fjern lokalt så galleriet oppdateres umiddelbart. Parent kan også
        // lytte på event for å rebygge sin egen library-state.
        this.library = this.library.filter((i) => i.id !== item.id);
        this.dispatchEvent(
          new CustomEvent<{ id: string; path: string }>('asset-deleted', {
            bubbles: true,
            composed: true,
            detail: { id: item.id, path: item.assets?.[0] ?? '' },
          }),
        );
      } catch (err) {
        console.error('[d-media-gallery] delete failed:', err);
        await AlertDialog.open({
          title: t('screens.deleteFailedTitle'),
          content: t('screens.deleteFailedGeneric'),
        });
      } finally {
        this.isDeleting = false;
      }
    }
  }

  private close(e: CustomEvent) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('close', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private deleteButtonClasses(disabled: boolean) {
    return {
      delete: true,
      disabled,
    };
  }

  private renderDeleteButton(item: LibraryItem, path: string) {
    if (item.standardItem) {
      return nothing;
    }
    return html`<button
      class=${classMap(this.deleteButtonClasses(!this.canDelete(item, path)))}
      aria-label=${t('screens.mediaGallery.deleteAriaLabel', { name: item.name })}
      @click=${(e: MouseEvent) => this.onDelete(e, item, path)}
    ></button>`;
  }

  private renderItem(item: LibraryItem) {
    const path = item.assets?.[0] ?? '';
    if (!path) return nothing;
    const isVideo = item.signageMediaType === 'video';
    if (isVideo) {
      // Thumbnail-prioritet: backend-generert data-URL (lett, ingen ekstra
      // nettverk) > selve videoen som <video preload="metadata"> (laster bare
      // første frame) > CSS-placeholder. Uten ready-sjekk pekte vi tidligere
      // <d-loading-image> rett på video-asseten, og hver tile genererte 60×404
      // mens backend konverterte. Nå rendrer vi <video>-fallback først når
      // assetAvailability bekrefter at pathen finnes. Optimistisk-lagt-til
      // items uten thumbnail viser placeholder til library-poll bekrefter.
      const url = resolveAssetUrl(path, this.assetBase);
      const ready = this.readyVideoPaths.has(path);
      return html`<div
        class="media video"
        @click=${() => this.select(path)}
        @mouseenter=${(e: MouseEvent) => this.startHoverPreview(e)}
        @mouseleave=${(e: MouseEvent) => this.stopHoverPreview(e)}
      >
        ${item.thumbnail
          ? html`<img class="thumbnail" src="${item.thumbnail}" alt="${item.name}" loading="lazy" decoding="async" />`
          : ready
            ? html`<video class="thumbnail" muted playsinline preload="metadata" src="${url}"></video>`
            : html`<div class="thumbnail-placeholder" aria-label="${item.name}"></div>`}
        <video class="hover-preview" muted playsinline loop preload="none" src="${url}"></video>
        ${this.renderDeleteButton(item, path)}
      </div>`;
    }
    // Thumbnail-prioritet: backend-generert data-URL > full-size via
    // d-loading-image (asset-availability-poll). Picker viser bare visuell
    // representasjon — klikk-handler bruker fortsatt path (assets[0]) som
    // kanonisk referanse, så slide-bygging og asset-usage-sjekk er uendret.
    return html`<div class="media" @click=${() => this.select(path)}>
      ${item.thumbnail
        ? html`<img class="thumbnail" src="${item.thumbnail}" alt="${item.name}" loading="lazy" decoding="async" />`
        : html`<d-loading-image
            src="${resolveAssetUrl(path, this.assetBase)}"
            asset-path="${path}"
            alt="${item.name}"
            loading="lazy"
          ></d-loading-image>`}
      ${this.renderDeleteButton(item, path)}
    </div>`;
  }

  render(): TemplateResult<1> {
    return html`
      <div class="header">
        <d-label
          label=${t('screens.mediaGallery.selectImage')}
          .labelAction=${t('screens.cancel')}
          label-action-plain
          @label-action=${(e: CustomEvent) => this.close(e)}
        ></d-label>
      </div>
      <div class="content">
        <input
          id="upload-input"
          type="file"
          accept="${ACCEPT_MIME}"
          hidden
          @change=${(e: Event) => this.onFileChange(e)}
        />
        <div class="media upload" @click=${() => this.onUpload()}>${t('screens.mediaGallery.upload')}</div>
        ${this.library
          // Bilde-pickeren skal kun vise media. Slide-items hører til
          // forslagspanelet ("Mine lysbilder"), og document/presentation osv.
          // er ikke valgbare som media i en slide.
          .filter((item) => item.signageMediaType === 'image' || item.signageMediaType === 'video')
          .map((item) => this.renderItem(item))}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-media-gallery': DMediaGallery;
  }
}
